import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import NeuralWord from "./NeuralWord";

const Hero = () => {
  return (
    <section id="top" className="relative pt-36 sm:pt-44 pb-28 px-6 overflow-hidden" data-testid="section-hero">
      <div aria-hidden className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-aura-cyan/10 blur-[120px] opacity-60" />
      <div aria-hidden className="absolute top-40 -right-32 w-[500px] h-[500px] rounded-full bg-aura-violet/20 blur-[160px] opacity-40" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="flex items-center gap-3 flex-wrap"
        >
          <span className="pill" data-testid="hero-status-pill">
            <span className="relative w-1.5 h-1.5 rounded-full bg-aura-cyan">
              <span className="absolute inset-0 rounded-full animate-ping bg-aura-cyan/60" />
            </span>
            Neural core online — v3.1
          </span>
          <span className="hidden sm:inline-flex pill border-panel/10 bg-panel/[0.02] text-whisper">
            Immersive Language Intelligence
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] }}
          className="headline mt-8 text-[clamp(48px,8.2vw,132px)] max-w-5xl"
          data-testid="hero-headline"
        >
          The tutor<br />
          who <em>never forgets</em><br />
          <span className="text-ink/85">you.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-8 max-w-xl text-[17px] sm:text-[18px] leading-[1.6] text-whisper"
        >
          A language tutor that builds a living memory of how you
          <NeuralWord>speak</NeuralWord>, where you hesitate, and what you
          want to say tomorrow. Every session continues yesterday.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/auth" className="btn-primary" data-testid="hero-cta-primary">
            <span>Begin your journey</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </Link>
          <a href="#voice" className="btn-ghost" data-testid="hero-cta-secondary">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            See how it works
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { k: "90+", l: "Languages" },
            { k: "∞", l: "Memory depth" },
            { k: "A1→C2", l: "CEFR coverage" },
            { k: "0.3s", l: "Voice latency" },
          ].map((s, i) => (
            <div key={s.l} className="relative px-5 py-5 rounded-2xl glass edge-glow" data-testid={`hero-metric-${i}`}>
              <div className="font-display text-4xl font-light tracking-tight text-ink">{s.k}</div>
              <div className="mt-1 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
